import React from 'react';
import proptypes from 'prop-types';
import {
  Card, Filmstrip, SkeletonBlock, SkeletonLine, Typography
} from '@one-thd/sui-atomic-components';
import { useImpression } from '@thd-olt-component-react/impression';

const {
  bool, node, string, shape, number, oneOfType
} = proptypes;

// Pages one pod at a time, like the other tabs' carousels.
const FILMSTRIP_BREAKPOINTS = {
  sm: { slidesPerView: 1, slidesPerGroup: 1 },
  md: { slidesPerView: 2, slidesPerGroup: 1 },
  lg: { slidesPerView: 3, slidesPerGroup: 1 },
  xl: { slidesPerView: 3, slidesPerGroup: 1 },
  '2xl': { slidesPerView: 3, slidesPerGroup: 1 },
};

const PodSkeleton = () => (
  <Card className="sui-w-full sui-h-full sui-rounded-md sui-p-4" style={{ minHeight: '25rem' }}>
    <SkeletonLine numberOfLines={2} />
    <div className="sui-mt-4">
      <SkeletonBlock aspect="wide" />
    </div>
  </Card>
);

// No border, like the other tabs' empty states.
const EmptyState = ({ text, innerRef, clickID }) => (
  <div
    data-testid="snapshot-pod-empty"
    className="sui-w-full sui-h-full sui-flex sui-items-center sui-justify-center sui-bg-primary sui-p-4"
    style={{ minHeight: '25rem' }}
    ref={innerRef}
    // eslint-disable-next-line react/no-unknown-property
    clickid={clickID}
  >
    <Typography variant="body-base" color="subtle">{text}</Typography>
  </div>
);

EmptyState.propTypes = {
  text: string.isRequired,
  innerRef: shape({}),
  clickID: string,
};

// Same empty state, observed for a viewport impression. A separate component so the
// useImpression hook only runs when there is something to track.
const TrackedEmptyState = ({ text, impression }) => {
  const { ref, clickID } = useImpression({ data: impression });
  return <EmptyState text={text} innerRef={ref} clickID={clickID} />;
};

TrackedEmptyState.propTypes = {
  text: string.isRequired,
  impression: shape({
    id: string,
    name: string,
    component: string,
    type: string,
    position: oneOfType([number, string]),
  }).isRequired,
};

/** The active tab's cards in a Filmstrip, with skeletons while loading. */
export const SnapshotPodPanel = ({
  children = null,
  loading = false,
  emptyText = 'Nothing to show right now.',
  emptyImpression = undefined,
}) => {
  const hasChildren = React.Children.count(children) > 0;

  if (!loading && !hasChildren) {
    return emptyImpression
      ? <TrackedEmptyState text={emptyText} impression={emptyImpression} />
      : <EmptyState text={emptyText} />;
  }

  return (
    // 24px above and below the tiles (Figma "Carousel"); the parent supplies the side inset.
    // Fills the panel so the tiles stretch. Descendant selectors because Filmstrip replaces
    // its className prop.
    <div
      className="sui-w-full sui-overflow-hidden sui-pt-6 sui-pb-6 sui-flex sui-flex-col
        [&>div]:sui-grow [&_.sui-carousel-scrollbar]:sui-h-full"
    >
      <Filmstrip
        slidesPerView={1}
        slidesPerGroup={1}
        peek="sm"
        spaceBetween={16}
        breakpoints={FILMSTRIP_BREAKPOINTS}
        disableMargin
      >
        {loading && !hasChildren
          // eslint-disable-next-line react/no-array-index-key
          ? [0, 1, 2].map((i) => <PodSkeleton key={`skeleton-${i}`} />)
          : children}
      </Filmstrip>
    </div>
  );
};

SnapshotPodPanel.propTypes = {
  loading: bool,
  children: node,
  emptyText: string,
  // When set, the empty state fires this viewport impression (the tab's null view).
  emptyImpression: shape({}),
};

SnapshotPodPanel.displayName = 'SnapshotPodPanel';
